import * as yup from 'yup';
import {
  MAXIMUM_TEXT_AREA_LENGTH,
  TASK_MAX_RECORDED_MINUTES,
} from '@common/types/constants.ts';
import {
  addOptionalNullableToSchema,
  requiredErrorMessage,
} from '@common/schema';

export const ACTIVITY_MINUTES_KEYS = [
  'plan',
  'reconciliate',
  'coordinate',
  'educate',
  'communicate',
] as const;

export const MAX_ACTIVITY_MINUTES = TASK_MAX_RECORDED_MINUTES;

export const emptyActivityMinutes: Record<
  (typeof ACTIVITY_MINUTES_KEYS)[number],
  number
> = {
  plan: 0,
  reconciliate: 0,
  coordinate: 0,
  educate: 0,
  communicate: 0,
};

export const staticFieldsSchema = yup.object().shape({
  static_notes: yup
    .string()
    .max(
      MAXIMUM_TEXT_AREA_LENGTH,
      `This input must be ${MAXIMUM_TEXT_AREA_LENGTH} characters or less. Please reduce the length to proceed.`,
    )
    .when('$hideDefaultControls', {
      is: false,
      then: schema => schema.required(requiredErrorMessage),
      otherwise: schema => addOptionalNullableToSchema(schema),
    }),

  static_note_type: yup
    .string()
    .oneOf(['Email', 'Phone call', 'In person', 'Reflection', 'Text', 'Other'])
    .when('$hideDefaultControls', {
      is: false,
      then: schema => schema.required('Notes are required'),
      otherwise: schema => addOptionalNullableToSchema(schema),
    }),

  static_notable_call: yup.boolean().when('$hideDefaultControls', {
    is: false,
    then: schema => schema.required(requiredErrorMessage),
    otherwise: schema => addOptionalNullableToSchema(schema),
  }),

  static_recorded_minutes: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' || originalValue === null ? undefined : value,
    )
    .integer('Minutes must be a whole integer')
    .max(
      TASK_MAX_RECORDED_MINUTES,
      `Minutes must be smaller or equal to ${TASK_MAX_RECORDED_MINUTES}`,
    )
    .min(0, 'Minutes must be greater or equal to 0')
    .when('$hideDefaultControls', {
      is: false,
      then: schema => schema.required(requiredErrorMessage),
      otherwise: schema => addOptionalNullableToSchema(schema),
    }),

  static_program_id: yup.string().nullable().optional(),

  static_activity_minutes: yup
    .object({
      plan: yup
        .number()
        .min(0, 'Minutes must be greater or equal to 0')
        .default(0),
      reconciliate: yup
        .number()
        .min(0, 'Minutes must be greater or equal to 0')
        .default(0),
      coordinate: yup
        .number()
        .min(0, 'Minutes must be greater or equal to 0')
        .default(0),
      educate: yup
        .number()
        .min(0, 'Minutes must be greater or equal to 0')
        .default(0),
      communicate: yup
        .number()
        .min(0, 'Minutes must be greater or equal to 0')
        .default(0),
    })
    .test(
      'sum-max',
      `The sum of all activities must be smaller or equal to ${TASK_MAX_RECORDED_MINUTES}`,
      value =>
        !value ||
        ACTIVITY_MINUTES_KEYS.reduce(
          (acc, key) => acc + (Number(value[key]) || 0),
          0,
        ) <= TASK_MAX_RECORDED_MINUTES,
    )
    .optional(),
});
